import React, { useState, useEffect } from "react";
import apiService from "../../../Services/apiServices";
import helper from "../../../Services/helper";
import globals from "../../../Services/globals";
import Search from "../../../PageComponents/Search/Search";
import XLSX from 'xlsx-js-style';

function VatAnnualReport() {
  const [saleRows, setSaleRows] = useState([]);
  const [purchaseRows, setPurchaseRows] = useState([]);
  const [saleMonthlyData, setSaleMonthlyData] = useState({});
  const [purchaseMonthlyData, setPurchaseMonthlyData] = useState({});
  const [yearInput, setYearInput] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const [showProgress, setShowProgress] = useState(false);
  const [vatReportMonthly, setVatReportMonthly] = useState({});
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const [settings, setSettings] = useState();

  useEffect(() => {
    globals().getSettings().then(r => { if (r) setSettings(r); });
    loadAllData();
    document.getElementById('navi')?.classList.add("nav-mobile");
    return () => { document.getElementById('navi')?.classList.remove("nav-mobile"); };
  }, [yearInput]);

  const loadAllData = async () => {
    setShowProgress(true);
    const [saleDefRes, purchaseDefRes] = await Promise.all([
      apiService().query('getSaleOrPurchase', `purchaseOrSale=1`),
      apiService().query('getSaleOrPurchase', `purchaseOrSale=0`)
    ]);
    setSaleRows(saleDefRes.success ? saleDefRes.data : []);
    setPurchaseRows(purchaseDefRes.success ? purchaseDefRes.data : []);

    const [saleResults, purchaseResults, vatResults] = await Promise.all([
      Promise.all(months.map(m => apiService().query('RptVatSaleReport', `CompanyBranch=0&YEAR=${yearInput}&StartMonth=${m}&EndMonth=${m}`))),
      Promise.all(months.map(m => apiService().query('RptVatPurchaseReport', `CompanyBranch=0&YEAR=${yearInput}&StartMonth=${m}&EndMonth=${m}`))),
      Promise.all(months.map(m => apiService().query('getVatDeclarationReport', `monthId=${m}&year=${yearInput}`)))
    ]);

    let sM = {}, pM = {}, vM = {};
    saleResults.forEach((r, i) => { sM[months[i]] = r.success && r.data?.[0] ? r.data[0] : {}; });
    purchaseResults.forEach((r, i) => { pM[months[i]] = r.success && r.data?.[0] ? r.data[0] : {}; });
    vatResults.forEach((r, i) => { vM[months[i]] = r.success ? r.data : []; });
    setSaleMonthlyData(sM);
    setPurchaseMonthlyData(pM);
    setVatReportMonthly(vM);
    setShowProgress(false);
  };

  const getVal = (md, num) => parseFloat(md?.["K" + num]) || 0;
  const rowTotal = (mD, num) => months.reduce((s, m) => s + getVal(mD[m], num), 0);
  const vatMonth = (rows, mD, m) => rows.reduce((t, r) => t + getVal(mD[m], r.number) * (r.vat / 100), 0);
  const vatTotal = (rows, mD) => months.reduce((s, m) => s + vatMonth(rows, mD, m), 0);
  const vdValRaw = (m, nv) => vatReportMonthly[m]?.find(f => f.numVat === nv)?.totalSum || 0;

  // Check if a month has any sale or purchase activity
  const monthHasData = (m) => {
    let hasSale = saleRows.some(r => getVal(saleMonthlyData[m], r.number) !== 0);
    let hasPurchase = purchaseRows.some(r => getVal(purchaseMonthlyData[m], r.number) !== 0);
    return hasSale || hasPurchase;
  };

  // Calculated VAT declaration values per month (same formulas as monthly report)
  const calcVatDecl = (m) => {
    // Nese muaji nuk ka te dhena shitjesh/blerjesh, mos llogarit
    if (!monthHasData(m)) return { 68: 0, 69: 0, 70: 0, 71: 0, 72: 0 };

    let vat30 = vatMonth(saleRows, saleMonthlyData, m);    // Total TVSH Shitje
    let vat67 = vatMonth(purchaseRows, purchaseMonthlyData, m); // Total TVSH Blerje
    let vat68 = vdValRaw(m, 68);                             // Teprica nga muaji kaluar (DB)
    let vat69 = (vat67 + vat68) - vat30 > 0 ? (vat67 + vat68) - vat30 : 0;
    let vat70 = vdValRaw(m, 70);                             // Kerkesa per rimbursim (DB)
    let vat71 = vat69 - vat70;
    let vat72 = (vat30 - vat67 - vat68) > 0 ? (vat30 - vat67 - vat68) : 0;
    return { 68: vat68, 69: vat69, 70: vat70, 71: vat71, 72: vat72 };
  };

  const vdVal = (m, nv) => calcVatDecl(m)[nv] || 0;
  const vdTotal = (nv) => months.reduce((s, m) => s + vdVal(m, nv), 0);

  const n = (v) => (!v || v === 0) ? '' : helper().formatNumber(v);

  const years = Array.from({ length: currentYear - 2024 + 1 }, (_, i) => 2024 + i);

  const exportToExcel = () => {
    let rows = [];
    let rowTypes = [];
    const numFmt = '#,##0.00';
    const colCount = 15; // name + NR + 12 months + total

    // Company info header
    let companyName = settings?.companyName || '';
    let fiscalNumber = settings?.fiscalNumber || '';
    let address = settings?.address || '';
    rows.push([`${companyName}  |  Nr. Fiskal: ${fiscalNumber}  |  ${address}`, '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    rowTypes.push('company');
    rows.push([`Tvsh Vjetore - ${yearInput}`, '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    rowTypes.push('title');
    rows.push(Array(colCount).fill(''));
    rowTypes.push('empty');

    // Helper to build a row array: [name, NR, m1..m12, total]
    const buildRow = (name, nr, monthVals, total) => {
      return [name, nr, ...monthVals, total];
    };

    const addSectionRows = (title, sectionRows, mData, totalLabel) => {
      // Header
      let hdr = [title, 'NR', ...months.map(m => String(m)), totalLabel];
      rows.push(hdr);
      rowTypes.push('header');

      // Data rows
      sectionRows.forEach(row => {
        let mVals = months.map(m => getVal(mData[m], row.number) || 0);
        let total = rowTotal(mData, row.number);
        rows.push(buildRow(`[${row.number}] ${row.name}`, row.number, mVals, total));
        rowTypes.push('data');
      });

      // Totali
      let totVals = months.map(m => sectionRows.reduce((s, r) => s + getVal(mData[m], r.number), 0));
      let grandTotal = sectionRows.reduce((s, r) => s + rowTotal(mData, r.number), 0);
      rows.push(buildRow('Totali:', '', totVals, grandTotal));
      rowTypes.push('total');

      // TVSH 18%
      let vat18 = sectionRows.filter(r => r.vat === 18);
      let v18Vals = months.map(m => vatMonth(vat18, mData, m));
      rows.push(buildRow('Total: T.V.SH. e llogartiur 18%', '', v18Vals, vatTotal(vat18, mData)));
      rowTypes.push('vat');

      // TVSH 8%
      let vat8 = sectionRows.filter(r => r.vat === 8);
      let v8Vals = months.map(m => vatMonth(vat8, mData, m));
      rows.push(buildRow('Tota: T.V.SH. e llogartiur 8%', '', v8Vals, vatTotal(vat8, mData)));
      rowTypes.push('vat');

      // Total TVSH
      let vtVals = months.map(m => vatMonth(sectionRows, mData, m));
      rows.push(buildRow('Total: T.V.SH. e llogartiur', '', vtVals, vatTotal(sectionRows, mData)));
      rowTypes.push('totalVat');
    };

    // SHITJET
    addSectionRows('Furnizimet (Shitjet) :', saleRows, saleMonthlyData, 'Total Shitja :');

    // BLERJET
    addSectionRows('Blerjet :', purchaseRows, purchaseMonthlyData, 'Total Blerje :');

    // VAT DECLARATION 68-72
    const vatDeclItems = [
      { num: 68, label: '[68] Teprica a TVSH-së e bartur nga muaji i kaluar', type: 'green' },
      { num: 69, label: '[69] Balanca kreditore e mbetur 69 = ((67+68)-30)>0', type: 'green' },
      { num: 70, label: '[70] Kërkesa për rimbursim', type: 'yellow' },
      { num: 71, label: '[71] Kreditimi i tvsh-së per muajin pasues 71 =(69-70)', type: 'green' },
      { num: 72, label: '[72] TVSH që duhet të paguhet 72 =(30-67-68)>0', type: 'red' },
    ];

    vatDeclItems.forEach(item => {
      let mVals = months.map(m => vdVal(m, item.num) || 0);
      let total = months.reduce((s, m) => s + (vdVal(m, item.num) || 0), 0);
      rows.push(buildRow(item.label, item.num, mVals, total));
      rowTypes.push('vd_' + item.type);
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
      { wch: 50 }, { wch: 5 },
      ...months.map(() => ({ wch: 14 })),
      { wch: 16 }
    ];

    // Style definitions
    const border = {
      top: { style: 'thin', color: { rgb: 'B0B0B0' } },
      bottom: { style: 'thin', color: { rgb: 'B0B0B0' } },
      left: { style: 'thin', color: { rgb: 'B0B0B0' } },
      right: { style: 'thin', color: { rgb: 'B0B0B0' } }
    };

    const styles = {
      header:    { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '1B2A4A' } }, alignment: { horizontal: 'center', vertical: 'center' }, border },
      headerL:   { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '1B2A4A' } }, alignment: { vertical: 'center' }, border },
      data:      { font: { sz: 10 }, border, alignment: { horizontal: 'right' }, numFmt },
      dataL:     { font: { sz: 10 }, border },
      total:     { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'D9E2F3' } }, border, alignment: { horizontal: 'right' }, numFmt },
      totalL:    { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'D9E2F3' } }, border },
      vat:       { font: { sz: 10 }, fill: { fgColor: { rgb: 'E8E8E8' } }, border, alignment: { horizontal: 'right' }, numFmt },
      vatL:      { font: { sz: 10 }, fill: { fgColor: { rgb: 'E8E8E8' } }, border, alignment: { horizontal: 'right' } },
      totalVat:  { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1B2A4A' } }, border, alignment: { horizontal: 'right' }, numFmt },
      totalVatL: { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1B2A4A' } }, border, alignment: { horizontal: 'right' } },
      vd_green:  { font: { bold: true, sz: 10, color: { rgb: '1A3C2A' } }, fill: { fgColor: { rgb: 'D4EDDA' } }, border, alignment: { horizontal: 'right' }, numFmt },
      vd_greenL: { font: { bold: true, sz: 10, color: { rgb: '1A3C2A' } }, fill: { fgColor: { rgb: 'D4EDDA' } }, border },
      vd_yellow: { font: { bold: true, sz: 10, color: { rgb: '5A4A00' } }, fill: { fgColor: { rgb: 'FFF3CD' } }, border, alignment: { horizontal: 'right' }, numFmt },
      vd_yellowL:{ font: { bold: true, sz: 10, color: { rgb: '5A4A00' } }, fill: { fgColor: { rgb: 'FFF3CD' } }, border },
      vd_red:    { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'A93226' } }, border, alignment: { horizontal: 'right' }, numFmt },
      vd_redL:   { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: 'A93226' } }, border },
    };

    // Extra styles for company/title
    const companyStyle = { font: { bold: true, sz: 13, color: { rgb: '1B2A4A' } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const titleStyle = { font: { bold: true, sz: 12, color: { rgb: '4E73DF' } }, alignment: { horizontal: 'left', vertical: 'center' } };

    // Apply styles
    for (let r = 0; r < rows.length; r++) {
      let type = rowTypes[r];
      for (let c = 0; c < colCount; c++) {
        let cellRef = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };

        if (type === 'company') {
          ws[cellRef].s = companyStyle;
        } else if (type === 'title') {
          ws[cellRef].s = titleStyle;
        } else if (type === 'empty') {
          // skip
        } else if (type === 'header') {
          ws[cellRef].s = c === 0 ? styles.headerL : styles.header;
        } else {
          let baseType = type;
          let isNumCol = c >= 2; // columns after NR are numbers
          if (isNumCol) {
            ws[cellRef].s = styles[baseType] || styles.data;
            if (typeof ws[cellRef].v === 'number') ws[cellRef].z = numFmt;
          } else {
            ws[cellRef].s = styles[baseType + 'L'] || styles[baseType] || styles.dataL;
          }
        }
      }
    }

    // Merge company name and title across all columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
    ];

    // Row heights
    ws['!rows'] = rowTypes.map(t => ({ hpt: t === 'company' ? 28 : t === 'title' ? 24 : t === 'header' ? 22 : 18 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tvsh Vjetore');
    XLSX.writeFile(wb, `Tvsh_Vjetore_${yearInput}.xlsx`);
  };

  // compact styles
  const b = '1px solid #8ca8c8';
  const hd = { backgroundColor: '#1e3a5f', color: '#e8f0fe', fontSize: '11px', fontWeight: 'bold', padding: '5px 3px', textAlign: 'center', whiteSpace: 'nowrap', border: b };
  const hdL = { ...hd, textAlign: 'left' };
  const cl = { padding: '2px 5px', fontSize: '11px', textAlign: 'right', border: b, whiteSpace: 'nowrap', backgroundColor: '#ffffff' };
  const clN = { ...cl, textAlign: 'left' };
  const clC = { ...cl, textAlign: 'center' };

  const DataTable = ({ title, rows, mData, totalCol }) => {
    const colTotal = (m) => rows.reduce((s, r) => s + getVal(mData[m], r.number), 0);
    const grand = rows.reduce((s, r) => s + rowTotal(mData, r.number), 0);
    const vat18 = rows.filter(r => r.vat === 18);
    const vat8 = rows.filter(r => r.vat === 8);

    return (
      <>
        {/* Header */}
        <tr>
          <td style={hdL}>{title}</td>
          <td style={hd}>NR</td>
          {months.map(m => <td key={m} style={hd}>{m}</td>)}
          <td style={{ ...hd, backgroundColor: '#16305a', minWidth: '90px' }}>{totalCol}</td>
        </tr>
        {/* Data rows */}
        {rows.map(row => (
          <tr key={row.number}>
            <td style={clN}>[{row.number}] {row.name}</td>
            <td style={clC}>{row.number}</td>
            {months.map(m => <td key={m} style={cl}>{n(getVal(mData[m], row.number))}</td>)}
            <td style={{ ...cl, fontWeight: 'bold' }}>{n(rowTotal(mData, row.number))}</td>
          </tr>
        ))}
        {/* Totali */}
        <tr>
          <td style={{ ...clN, backgroundColor: '#c4d9f0', fontWeight: 'bold', color: '#0d2b55' }} colSpan="2">Totali:</td>
          {months.map(m => <td key={m} style={{ ...cl, backgroundColor: '#c4d9f0', fontWeight: 'bold', color: '#0d2b55' }}>{n(colTotal(m))}</td>)}
          <td style={{ ...cl, backgroundColor: '#c4d9f0', fontWeight: 'bold', color: '#0d2b55' }}>{n(grand)}</td>
        </tr>
        {/* TVSH 18% */}
        <tr>
          <td style={{ ...cl, textAlign: 'left', backgroundColor: '#f0f5fb', color: '#2c4870' }} colSpan="2">Total: T.V.SH. e llogartiur 18%</td>
          {months.map(m => <td key={m} style={{ ...cl, backgroundColor: '#f0f5fb', color: '#2c4870' }}>{n(vatMonth(vat18, mData, m))}</td>)}
          <td style={{ ...cl, fontWeight: 'bold', backgroundColor: '#f0f5fb', color: '#2c4870' }}>{n(vatTotal(vat18, mData))}</td>
        </tr>
        {/* TVSH 8% */}
        <tr>
          <td style={{ ...cl, textAlign: 'left', backgroundColor: '#f0f5fb', color: '#2c4870' }} colSpan="2">Tota: T.V.SH. e llogartiur 8%</td>
          {months.map(m => <td key={m} style={{ ...cl, backgroundColor: '#f0f5fb', color: '#2c4870' }}>{n(vatMonth(vat8, mData, m))}</td>)}
          <td style={{ ...cl, fontWeight: 'bold', backgroundColor: '#f0f5fb', color: '#2c4870' }}>{n(vatTotal(vat8, mData))}</td>
        </tr>
        {/* Total TVSH - dark row */}
        <tr>
          <td style={{ ...cl, textAlign: 'left', backgroundColor: '#1e3a5f', color: '#d4e4f9', fontWeight: 'bold', border: b }} colSpan="2">Total: T.V.SH. e llogartiur</td>
          {months.map(m => <td key={m} style={{ ...cl, backgroundColor: '#1e3a5f', color: '#d4e4f9', fontWeight: 'bold', border: b }}>{n(vatMonth(rows, mData, m))}</td>)}
          <td style={{ ...cl, backgroundColor: '#16305a', color: '#d4e4f9', fontWeight: 'bold', border: b }}>{n(vatTotal(rows, mData))}</td>
        </tr>
      </>
    );
  };

  return (
    <div className="container-fluid px-1 mt-2">
      <Search back="#/reportsMap/2" />

      {showProgress &&
        <div style={{ position: 'fixed', top: '50px', right: '40px', zIndex: 1000, color: '#4E73DF', fontSize: 18 }}>
          {helper().translate('LOADING')} ...
        </div>
      }

      {/* Title */}
      <div className="text-center mb-2">
        <h6 className="font-weight-bold text-primary m-0">Tvsh Vjetore - {yearInput}</h6>
      </div>

      {/* Toolbar */}
      <div className="d-flex no-print mb-1 align-items-center" style={{ gap: '6px' }}>
        <select className="form-control form-control-sm" style={{ width: '70px', fontSize: '12px' }}
          value={yearInput} onChange={(e) => setYearInput(parseInt(e.target.value, 10))}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => window.print()} className="btn btn-dark btn-sm" style={{ fontSize: '12px' }}>
          <i className="fas fa-print mr-1" /> PRINTO
        </button>
        <button onClick={exportToExcel} className="btn btn-success btn-sm" style={{ fontSize: '12px' }}>
          <i className="fas fa-file-excel mr-1" /> EXCEL
        </button>
      </div>

      {/* Main table - everything in one table for alignment */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px' }}>
          <tbody>
            {/* SHITJET */}
            <DataTable title="Furnizimet (Shitjet) :" rows={saleRows} mData={saleMonthlyData} totalCol="Total Shitja :" />

            {/* BLERJET */}
            <DataTable title="Blerjet :" rows={purchaseRows} mData={purchaseMonthlyData} totalCol="Total Blerje :" />

            {/* VAT DECLARATION 68-72 */}
            {[
              { num: 68, label: '[68] Teprica a TVSH-së e bartur nga muaji i kaluar', bg: '#a8d5b5', fg: '#0e3320' },
              { num: 69, label: '[69] Balanca kreditore e mbetur 69 = ((67+68)-30)>0', bg: '#a8d5b5', fg: '#0e3320' },
              { num: 70, label: '[70] Kërkesa për rimbursim', bg: '#f5d87a', fg: '#4a3500' },
              { num: 71, label: '[71] Kreditimi i tvsh-së per muajin pasues 71 =(69-70)', bg: '#a8d5b5', fg: '#0e3320' },
              { num: 72, label: '[72] TVSH që duhet të paguhet 72 =(30-67-68)>0', bg: '#1e3a5f', fg: '#e8f0fe' },
            ].map((row, idx) => {
              const cellStyle = { padding: '1px 4px', fontSize: '11px', textAlign: 'right', border: b, whiteSpace: 'nowrap', backgroundColor: row.bg, color: row.fg, fontWeight: 'bold' };
              return (
                <tr key={row.num}>
                  <td style={{ ...cellStyle, textAlign: 'left' }}>{row.label}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{row.num}</td>
                  {months.map(m => <td key={m} style={cellStyle}>{n(vdVal(m, row.num))}</td>)}
                  <td style={{ ...cellStyle, backgroundColor: row.num === 72 ? '#7b1a14' : row.num === 70 ? '#e8c24a' : '#7fbf96', color: row.num === 72 ? '#fff' : row.fg }}>{n(vdTotal(row.num))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VatAnnualReport;
